import { fetchProfileMedia } from './profileMedia';
import {
  fetchMediaBlob,
  downloadItemsAsZip,
  mapPool,
  MEDIA_FETCH_CONCURRENCY,
} from './zipDownload';
import { getImageExtension, sanitizeForFilename } from './mediaUtils';
import type { ProfileMediaItem, ProfileMediaPage } from './mediaContracts';

export type ExtractDestination = 'folder' | 'zip';

export type ExtractPhase =
  | 'idle'
  | 'discovering'
  | 'downloading'
  | 'packing'
  | 'done'
  | 'cancelled'
  | 'error';

export interface ExtractProgress {
  phase: ExtractPhase;
  discovered: number;
  saved: number;
  failed: number;
  estimatedTotal?: number;
  label?: string;
  pagesFetched: number;
  destination: ExtractDestination;
  folderName?: string;
  zipNames?: string[];
  message?: string;
}

export interface ExtractOptions {
  handle: string;
  destination: ExtractDestination;
  /** Items already loaded in the gallery. */
  seedItems?: ProfileMediaItem[];
  /** Cursor to continue from; null means timeline already exhausted. */
  seedCursor?: string | null;
  estimatedTotal?: number;
  pageSize?: number;
  zipChunkSize?: number;
  signal?: AbortSignal;
  onProgress?: (p: ExtractProgress) => void;
  onItemsDiscovered?: (items: ProfileMediaItem[], nextCursor: string | null) => void;
}

export interface ExtractResult {
  saved: number;
  failed: number;
  discovered: number;
  destination: ExtractDestination;
  folderName?: string;
  zipNames?: string[];
  cancelled: boolean;
}

export type DirectoryHandle = {
  name: string;
  getFileHandle: (
    name: string,
    options?: { create?: boolean }
  ) => Promise<{
    createWritable: () => Promise<{
      write: (data: Blob) => Promise<void>;
      close: () => Promise<void>;
    }>;
  }>;
};

declare global {
  interface Window {
    showDirectoryPicker?: (options?: {
      mode?: 'read' | 'readwrite';
      startIn?: string;
    }) => Promise<DirectoryHandle>;
  }
}

export const supportsDirectoryPicker = (): boolean =>
  typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';

export async function pickExtractFolder(): Promise<DirectoryHandle> {
  if (!supportsDirectoryPicker()) {
    throw new Error(
      'Folder save requires Chrome or Edge. Use ZIP download instead, or switch browsers.'
    );
  }
  return window.showDirectoryPicker!({ mode: 'readwrite' });
}

const itemDedupeKey = (item: ProfileMediaItem): string =>
  `${item.tweetId}|${item.downloadUrl}`;

const filenameForItem = (item: ProfileMediaItem, used: Set<string>): string => {
  const ext =
    item.type === 'photo' ? getImageExtension(item.downloadUrl) : 'mp4';
  const base = sanitizeForFilename(
    `${item.author || 'x'}_${item.tweetId || 'media'}_${item.type}`
  );
  let name = `${base}.${ext}`;
  if (used.has(name)) {
    let n = 2;
    while (used.has(`${base}_${n}.${ext}`)) n++;
    name = `${base}_${n}.${ext}`;
  }
  used.add(name);
  return name;
};

const throwIfAborted = (signal?: AbortSignal) => {
  if (signal?.aborted) {
    const err = new Error('Extraction cancelled');
    err.name = 'AbortError';
    throw err;
  }
};

async function saveItemToFolder(
  folder: DirectoryHandle,
  item: ProfileMediaItem,
  filename: string
): Promise<void> {
  const blob = await fetchMediaBlob(item.downloadUrl);
  const fileHandle = await folder.getFileHandle(filename, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(blob);
  await writable.close();
}

/**
 * One-button account extract: walk the public media timeline (paginating as
 * needed) and save every file to a chosen folder or chunked ZIP downloads.
 * Folder mode saves each page as it is discovered so files appear immediately.
 */
export async function extractAllAccountMedia(
  options: ExtractOptions
): Promise<ExtractResult> {
  const {
    handle,
    destination,
    seedItems = [],
    seedCursor = null,
    estimatedTotal,
    pageSize = 40,
    zipChunkSize = 80,
    signal,
    onProgress,
    onItemsDiscovered,
  } = options;

  const clean = handle.replace(/^@/, '').trim();
  if (!clean) throw new Error('Please enter a valid @handle');

  let folder: DirectoryHandle | null = null;
  let folderName: string | undefined;

  if (destination === 'folder') {
    folder = await pickExtractFolder();
    folderName = folder.name;
  }

  const seen = new Set<string>();
  const usedNames = new Set<string>();
  let discovered = 0;
  let saved = 0;
  let failed = 0;
  let pagesFetched = 0;
  const zipNames: string[] = [];
  let zipBuffer: ProfileMediaItem[] = [];
  let zipPart = 1;

  const emit = (partial: Partial<ExtractProgress>) => {
    onProgress?.({
      phase: 'discovering',
      discovered,
      saved,
      failed,
      estimatedTotal,
      pagesFetched,
      destination,
      folderName,
      zipNames: zipNames.length ? [...zipNames] : undefined,
      ...partial,
    });
  };

  const processFolderBatch = async (batch: ProfileMediaItem[]) => {
    if (!folder) return;

    // Allocate names sequentially, then fetch/write with a concurrency pool.
    const planned = batch.map((item) => ({
      item,
      filename: filenameForItem(item, usedNames),
    }));

    await mapPool(
      planned,
      MEDIA_FETCH_CONCURRENCY,
      async ({ item, filename }) => {
        throwIfAborted(signal);
        try {
          await saveItemToFolder(folder!, item, filename);
          return { ok: true as const, filename };
        } catch (err) {
          if ((err as Error)?.name === 'AbortError') throw err;
          console.error(`Failed to save ${filename}:`, err);
          return { ok: false as const, filename };
        }
      },
      (_index, result) => {
        if (result.ok) saved += 1;
        else failed += 1;
        emit({
          phase: 'downloading',
          discovered,
          saved,
          failed,
          label: result.filename,
        });
      }
    );
  };

  const flushZipBuffer = async (force = false) => {
    while (
      zipBuffer.length >= zipChunkSize ||
      (force && zipBuffer.length > 0)
    ) {
      throwIfAborted(signal);
      const chunk = zipBuffer.splice(0, zipChunkSize);
      const partLabel = force && zipBuffer.length === 0 && zipPart === 1 && zipNames.length === 0
        ? `${clean}_all_media.zip`
        : `${clean}_all_media_part_${zipPart}.zip`;

      const zipItems = chunk.map((item) => ({
        url: item.downloadUrl,
        filename: filenameForItem(item, usedNames),
      }));

      emit({
        phase: 'packing',
        discovered,
        saved,
        failed,
        label: partLabel,
        zipNames: [...zipNames],
      });

      try {
        const baseSaved = saved;
        const baseDiscovered = discovered;
        const baseFailed = failed;
        const result = await downloadItemsAsZip(zipItems, partLabel, (p) => {
          emit({
            phase: 'packing',
            discovered: baseDiscovered,
            saved: baseSaved + p.current,
            failed: baseFailed,
            label: p.label || partLabel,
            zipNames: [...zipNames],
          });
        });
        saved += result.saved;
        failed += result.total - result.saved;
        zipNames.push(result.zipName);
        zipPart += 1;
      } catch (err) {
        if ((err as Error)?.name === 'AbortError') throw err;
        console.error(`Failed to pack ${partLabel}:`, err);
        failed += chunk.length;
      }
    }
  };

  const ingest = async (items: ProfileMediaItem[]) => {
    const fresh: ProfileMediaItem[] = [];
    for (const item of items) {
      const key = itemDedupeKey(item);
      if (seen.has(key) || !item.downloadUrl) continue;
      seen.add(key);
      fresh.push(item);
    }
    if (fresh.length === 0) return;

    discovered += fresh.length;
    emit({
      phase: destination === 'folder' ? 'downloading' : 'discovering',
      discovered,
      label: `Found ${discovered} items…`,
    });

    if (destination === 'folder') {
      await processFolderBatch(fresh);
    } else {
      zipBuffer.push(...fresh);
      await flushZipBuffer(false);
    }
  };

  emit({ phase: 'discovering', label: 'Scanning media timeline…' });

  // Process anything already loaded in the gallery first.
  if (seedItems.length > 0) {
    await ingest(seedItems);
  }

  // Continue pagination from seed cursor, or start fresh when empty.
  let cursor: string | null = seedItems.length > 0 ? seedCursor ?? null : null;

  if (seedItems.length === 0) {
    throwIfAborted(signal);
    const firstPage: ProfileMediaPage = await fetchProfileMedia(
      clean,
      null,
      pageSize
    );
    pagesFetched += 1;
    cursor = firstPage.nextCursor;
    onItemsDiscovered?.(firstPage.items, firstPage.nextCursor);
    await ingest(firstPage.items);
    emit({ pagesFetched, discovered });
  }

  while (cursor) {
    throwIfAborted(signal);
    const nextCursor: string = cursor;
    const page: ProfileMediaPage = await fetchProfileMedia(
      clean,
      nextCursor,
      pageSize
    );
    pagesFetched += 1;
    cursor = page.nextCursor;
    onItemsDiscovered?.(page.items, page.nextCursor);
    await ingest(page.items);
    emit({ pagesFetched, discovered });
  }

  if (destination === 'zip') {
    await flushZipBuffer(true);
  }

  if (discovered === 0) {
    throw new Error(`No public media found for @${clean}`);
  }

  emit({
    phase: 'done',
    discovered,
    saved,
    failed,
    label: 'Complete',
    zipNames: zipNames.length ? zipNames : undefined,
    message:
      failed > 0
        ? `Saved ${saved} of ${discovered} files (${failed} failed)`
        : `Saved ${saved} files`,
  });

  return {
    saved,
    failed,
    discovered,
    destination,
    folderName,
    zipNames: zipNames.length ? zipNames : undefined,
    cancelled: false,
  };
}
